/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { connect } from 'react-redux'
import { getSelfUser } from '@sportninja/common/selectors/users'
import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from 'react'
import { t } from '@sportninja/common/i18n'
import { media, Mobile } from '../../components/Responsive'
import UserSidebar from './UserSidebar'
import Helmet from '../../components/Helmet'
import { HomePaymentBanner } from '../RegistrationPayment/HomePaymentBanner'
import { HomeBannerParticipantsLimit } from '../../components/HomeBannerParticipantsLimit'
import PostInput from 'src/components/PostInput'
import HighlightWidget from './components/HighlightWidget'
import FeedPage from 'src/components/FeedPage'
import FeedPostCreateModal from 'src/components/FeedPostCreateModal'
import OnboardingWizardModal from '../../components/ts-components/OnboardingWizardModal'
import ContinueTutorialBanner from '../../components/ContinueTutorialBanner'
import { useOnboardingTutorial } from '../../hooks/useOnboardingTutorial'
import { useSelector, useDispatch } from 'src/hooks/useRedux'
import accountActions from '@sportninja/common/actions/account'
import { isCanlan } from '@sportninja/common/utils/customer-name'
import TimezoneCheck from './components/TimezoneCheck'
import PendingInvitationsCard from 'src/features/notifications/components/PendingInvitationsCard'
import PendingInvitationsModal from 'src/features/notifications/components/PendingInvitationsModal'
import { usePendingInvitationsPopup } from 'src/features/notifications/hooks/usePendingInvitationsPopup'

const AccountType = {
  FREE: 1,
  PROFESSIONAL: 2,
  ULTIMATE: 3,
}

const Home = ({ user = {}, me = {}, isLoggedIn }) => {
  const dispatch = useDispatch()
  const refreshNewFeed = useRef(() => {})
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isTimezoneCheckOpen, setIsTimezoneCheckOpen] = useState(false)
  const [postToEdit, setPostToEdit] = useState(null)
  const [isLoadingFeed, setIsLoadingFeed] = useState(false)
  const [sidebarEmpty, setSidebarEmpty] = useState(false)
  const account = useSelector((state) => state.account._meAccount)

  useLayoutEffect(() => {
    if (me?.account_id) {
      dispatch(accountActions.fetchAccount.request(null, null, me.account_id))
    }
  }, [me?.account_id])

  // Use the onboarding tutorial hook
  const {
    shouldShowModal,
    shouldShowBanner,
    settled: onboardingSettled,
    updateTutorialStatus,
    openModal: openOnboardingModal,
    closeModal: closeOnboardingModal,
  } = useOnboardingTutorial({ me, isLoggedIn })

  // SN-7849: the invitations still waiting, once per sign-in. Onboarding goes
  // first, and the popup never opens over another Home modal.
  const pendingUserId = isLoggedIn ? me?.id : null
  const pendingInvitationsPopup = usePendingInvitationsPopup({
    userId: pendingUserId,
    onboardingSettled,
    otherModalOpen: shouldShowModal || isModalOpen || isTimezoneCheckOpen,
  })

  const handleOnboardingSkip = useCallback(async () => {
    await updateTutorialStatus('skipped')
  }, [updateTutorialStatus])

  const handleOnboardingComplete = useCallback(async () => {
    await updateTutorialStatus('completed')
  }, [updateTutorialStatus])

  useEffect(() => {
    const feedPage = document.getElementById('feed-page')
    if (isModalOpen) {
      feedPage.style.display = 'none'
    } else {
      feedPage.style.display = 'block'
    }
  }, [isModalOpen])

  const isBusinessAccount = useMemo(() => {
    return me?.account_id && me?.account_type && me?.account_type > 1
  }, [me])

  const shouldDisplayUpgradeBanner = useMemo(() => {
    return (
      Number(account?.subscription_type?.id) === AccountType.FREE &&
      isBusinessAccount &&
      !isCanlan
    )
  }, [account?.subscription_type?.id, isBusinessAccount])

  useEffect(() => {
    localStorage.removeItem('isLightMode')
  }, [])

  // Ctrl+T and Ctrl+Shift+T belong to the browser (new tab, reopen tab).
  // Shift+Y is free: Ctrl on Windows, Command on Mac, same split as the
  // sidebar shortcut. Only the home screen listens for it.
  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.repeat || event.altKey || event.key.toLowerCase() !== 'y') {
        return
      }
      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0
      const isShortcut = isMac
        ? event.metaKey && event.shiftKey && !event.ctrlKey
        : event.ctrlKey && event.shiftKey && !event.metaKey
      if (!isShortcut) {
        return
      }
      const target = event.target
      if (
        target instanceof HTMLElement &&
        target.closest('input, textarea, select, [contenteditable="true"]')
      ) {
        return
      }
      event.preventDefault()
      setIsTimezoneCheckOpen(true)
    }

    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [])

  return (
    <div
      css={css`
        display: flex;
        flex: 1;
        width: 100%;
        margin-top: 16px;
      `}
    >
      <TimezoneCheck
        isOpen={isTimezoneCheckOpen}
        onClose={() => setIsTimezoneCheckOpen(false)}
      />
      <OnboardingWizardModal
        isOpen={shouldShowModal}
        onClose={closeOnboardingModal}
        onSkip={handleOnboardingSkip}
        onComplete={handleOnboardingComplete}
      />
      <PendingInvitationsModal
        isOpen={pendingInvitationsPopup.isOpen}
        invitations={pendingInvitationsPopup.invitations}
        onClose={pendingInvitationsPopup.close}
      />
      <Helmet
        title={t('util:capitalize', {
          text: t('common:home'),
        })}
      />
      <Mobile>
        {(isMobile) => {
          return (
            <>
              <FeedPostCreateModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                refreshNewFeed={refreshNewFeed.current}
                postToEdit={postToEdit}
                setPostToEdit={setPostToEdit}
              />

              <div
                css={css`
                  display: flex;
                  justify-content: center;
                  width: 100%;
                `}
              >
                <div
                  css={css`
                    margin-right: 8px;
                    width: 100%;
                    max-width: 780px;
                    ${sidebarEmpty
                      ? 'margin-right: 55px;'
                      : 'margin-right: 8px;'}
                    ${media.mobile} {
                      margin-left: 0 !important;
                      margin-right: 0 !important;
                      width: calc(100% - 32px);
                    }
                  `}
                >
                  <div
                    css={css`
                      display: flex;
                      flex-direction: column;
                      gap: 16px;
                    `}
                  >
                    <HomeBannerParticipantsLimit />
                    {isMobile && shouldShowBanner ? (
                      <div
                        css={css`
                          margin-bottom: -8px;
                        `}
                      >
                        <ContinueTutorialBanner
                          onContinue={openOnboardingModal}
                          onDismiss={handleOnboardingComplete}
                        />
                      </div>
                    ) : null}
                    <HomePaymentBanner />
                    <PendingInvitationsCard userId={pendingUserId} />
                    {!isLoadingFeed ? (
                      <PostInput
                        onInputClick={() => setIsModalOpen(true)}
                        avatarUrl={user?.imageId}
                      />
                    ) : null}
                    {!isLoadingFeed ? <HighlightWidget /> : null}
                    <FeedPage
                      avatarUrl={user?.imageId}
                      refreshNewFeed={refreshNewFeed}
                      user={user}
                      setPostToEdit={setPostToEdit}
                      setModalOpen={setIsModalOpen}
                      setIsLoadingFeed={setIsLoadingFeed}
                      setIsModalOpen={setIsModalOpen}
                      isModalOpen={isModalOpen}
                    />
                  </div>
                </div>
                {!isMobile ? (
                  <UserSidebar
                    shouldShowOnboardingBanner={shouldShowBanner}
                    onContinueOnboarding={openOnboardingModal}
                    onDismissOnboarding={handleOnboardingComplete}
                    setSidebarEmpty={setSidebarEmpty}
                    shouldDisplayUpgradeBanner={shouldDisplayUpgradeBanner}
                  />
                ) : null}
              </div>
            </>
          )
        }}
      </Mobile>
    </div>
  )
}

const mapStateToProps = (state) => {
  return {
    user: getSelfUser(state),
    me: getSelfUser(state),
    isLoggedIn: state.auth.login.success,
  }
}

export default connect(mapStateToProps)(Home)
